// Bounds computation for node cache canvas dimensions and element positioning

import { measureTextDimensions } from '@/services/canvas/text/CanvasTextService';
import { TEXT_POSITION } from '@/models/TEXT_POSITION';
import { TEXT_ALIGN } from '@/models/TEXT_ALIGN';
import type {
  ShapeBounds, ShadowBounds, ImageBounds, TitleBounds, ComputedPosition,
} from '@/modules/Node';

const SHADOW_BLUR_MULTIPLIER = 2;

interface BoundsState {
  width: number;
  height: number;
  border: boolean;
  borderWidth: number;
  shadow: boolean;
  shadowBlur: number;
  shadowX: number;
  shadowY: number;
  image: HTMLImageElement | HTMLCanvasElement | null;
  mediaSize: number;
  mediaCropEnabled: boolean;
  title: string | null;
  titlePosition: string;
  textAlign: string;
  fontSize: number;
  fontColor: string | null;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  lineHeight: number;
  xCache: number | null;
  yCache: number | null;
  editingAppearance: unknown;
  editingContent: unknown;
}

export function computeShapeBounds(state: BoundsState): ShapeBounds {
  let width = state.width;
  let height = state.height;

  if (state.border) {
    width += state.borderWidth * 2;
    height += state.borderWidth * 2;
  }

  return { width, height, x: width / 2, y: height / 2 };
}

export function computeShadowBounds(state: BoundsState, shapeBounds: ShapeBounds): ShadowBounds {
  if (!state.shadow) {
    const bounds: ShadowBounds = {
      width: shapeBounds.width,
      height: shapeBounds.height,
      x: shapeBounds.x - shapeBounds.width / 2,
      y: shapeBounds.y - shapeBounds.height / 2,
      blurWidth: 0,
      shadowShiftX: 0,
      shadowShiftY: 0,
    };
    return bounds;
  }

  const blurWidth = state.shadowBlur * SHADOW_BLUR_MULTIPLIER;
  return {
    blurWidth,
    shadowShiftX: state.shadowX,
    shadowShiftY: state.shadowY,
    width: shapeBounds.width + blurWidth * 2,
    height: shapeBounds.height + blurWidth * 2,
    x: state.shadowX - blurWidth,
    y: state.shadowY - blurWidth,
  };
}

export function computeMediaBounds(state: BoundsState, shapeBounds: ShapeBounds): ImageBounds {
  const { width: shapeWidth, height: shapeHeight } = shapeBounds;

  if (state.image && state.image.width) {
    const imageRatio = state.image.width / state.image.height;
    const shapeRatio = shapeWidth / shapeHeight;
    const imageSizeMultiplier = state.mediaSize / 100;
    const scaleFactor = imageRatio > shapeRatio ? state.width / state.image.width : state.height / state.image.height;

    const width = state.image.width * scaleFactor * imageSizeMultiplier;
    const height = state.image.height * scaleFactor * imageSizeMultiplier;
    const x = shapeWidth / 2 - width / 2;
    const y = shapeHeight / 2 - height / 2;

    return {
      width,
      height,
      x,
      y,
      offsetX: !state.mediaCropEnabled && x < 0 ? x : 0,
      offsetY: !state.mediaCropEnabled && y < 0 ? y : 0,
    };
  }

  return { width: shapeWidth, height: shapeHeight, x: 0, y: 0, offsetX: 0, offsetY: 0 };
}

export function computeTitleData(
  ctx: CanvasRenderingContext2D,
  state: BoundsState,
  shapeBounds: ShapeBounds,
): ComputedPosition {
  const fontConfig = {
    fontSize: state.fontSize,
    fontFamily: state.fontFamily || 'Arial',
    fontWeight: state.fontWeight,
    fontStyle: state.fontStyle,
    lineHeight: state.fontSize * state.lineHeight,
  };

  const { width: shapeWidth, height: shapeHeight } = shapeBounds;

  let titleWidth: number | null = null;
  if (state.titlePosition === TEXT_POSITION.CENTER.VALUE) titleWidth = shapeWidth;

  const { textWidth, textHeight } = measureTextDimensions(ctx, state.title, fontConfig, titleWidth);
  const borderlessDimensionsNeeded = state.titlePosition !== TEXT_POSITION.CENTER.VALUE;
  const { textWidth: textWidthBorderless, textHeight: textHeightBorderless } = borderlessDimensionsNeeded
    ? measureTextDimensions(ctx, state.title, fontConfig)
    : { textWidth, textHeight };

  const isTitleOverflow = textHeight > shapeHeight;
  const computedBorder = state.border ? state.borderWidth : 0;
  const padding = 5;

  const computedPosition: ComputedPosition = {
    x: null,
    y: null,
    initialX: null,
    initialY: null,
    width: textWidth,
    height: textHeight,
    isTextOverflow: false,
    vAlign: TEXT_POSITION.CENTER.VALUE,
    align: null,
  };

  switch (state.titlePosition) {
    case TEXT_POSITION.TOP_LEFT.VALUE:
      computedPosition.x = state.xCache! - state.width / 2 - computedBorder - padding - textWidth;
      computedPosition.y = state.yCache! - state.height / 2 - textHeight - computedBorder - padding;
      computedPosition.initialX = - padding - textWidth;
      computedPosition.initialY = - textHeight - padding;
      computedPosition.vAlign = TEXT_POSITION.BOTTOM.VALUE;
      computedPosition.align = TEXT_ALIGN.RIGHT.VALUE;
      break;
    case TEXT_POSITION.TOP.VALUE:
      computedPosition.x = state.xCache! - textWidth / 2;
      computedPosition.y = state.yCache! - state.height / 2 - padding - computedBorder - textHeight;
      computedPosition.initialX = shapeWidth / 2 - textWidth / 2;
      computedPosition.initialY = - padding - textHeight;
      computedPosition.vAlign = TEXT_POSITION.BOTTOM.VALUE;
      computedPosition.align = TEXT_ALIGN.CENTER.VALUE;
      break;
    case TEXT_POSITION.TOP_RIGHT.VALUE:
      computedPosition.x = state.xCache! + state.width / 2 + computedBorder + padding;
      computedPosition.y = state.yCache! - state.height / 2 - textHeight - computedBorder - padding;
      computedPosition.initialX = shapeWidth + padding;
      computedPosition.initialY = - textHeight - padding;
      computedPosition.vAlign = TEXT_POSITION.BOTTOM.VALUE;
      computedPosition.align = TEXT_ALIGN.LEFT.VALUE;
      break;
    case TEXT_POSITION.LEFT.VALUE:
      computedPosition.x = state.xCache! - state.width / 2 - computedBorder - padding - textWidth;
      computedPosition.y = state.yCache! - textHeight / 2;
      computedPosition.initialX = - padding - textWidth;
      computedPosition.initialY = shapeHeight / 2 - textHeight / 2;
      computedPosition.align = TEXT_ALIGN.RIGHT.VALUE;
      break;
    case TEXT_POSITION.CENTER.VALUE:
      computedPosition.x = state.xCache! - state.width / 2;
      computedPosition.y = state.yCache! - state.height / 2;
      computedPosition.initialX = isTitleOverflow ? (shapeWidth - textWidthBorderless) / 2 : 0;
      computedPosition.initialY = isTitleOverflow ? (shapeHeight - textHeightBorderless) / 2 : 0;
      computedPosition.width = isTitleOverflow ? textWidthBorderless : shapeWidth;
      computedPosition.height = isTitleOverflow ? textHeightBorderless : shapeHeight;
      computedPosition.isTextOverflow = true;
      computedPosition.align = state.textAlign;
      break;
    case TEXT_POSITION.RIGHT.VALUE:
      computedPosition.x = state.xCache! + state.width / 2 + computedBorder + padding;
      computedPosition.y = state.yCache! - textHeight / 2;
      computedPosition.initialX = shapeWidth + padding;
      computedPosition.initialY = shapeHeight / 2 - textHeight / 2;
      computedPosition.align = TEXT_ALIGN.LEFT.VALUE;
      break;
    case TEXT_POSITION.BOTTOM_LEFT.VALUE:
      computedPosition.x = state.xCache! - state.width / 2 - computedBorder - padding - textWidth;
      computedPosition.y = state.yCache! + state.height / 2 + computedBorder + padding;
      computedPosition.initialX = - padding - textWidth;
      computedPosition.initialY = shapeHeight + padding;
      computedPosition.vAlign = TEXT_POSITION.TOP.VALUE;
      computedPosition.align = TEXT_ALIGN.RIGHT.VALUE;
      break;
    case TEXT_POSITION.BOTTOM.VALUE:
      computedPosition.x = state.xCache! - textWidth / 2;
      computedPosition.y = state.yCache! + state.height / 2 + computedBorder + padding;
      computedPosition.initialX = shapeWidth / 2 - textWidth / 2;
      computedPosition.initialY = shapeHeight + padding;
      computedPosition.vAlign = TEXT_POSITION.TOP.VALUE;
      computedPosition.align = TEXT_ALIGN.CENTER.VALUE;
      break;
    case TEXT_POSITION.BOTTOM_RIGHT.VALUE:
      computedPosition.x = state.xCache! + state.width / 2 + computedBorder + padding;
      computedPosition.y = state.yCache! + state.height / 2 + computedBorder + padding;
      computedPosition.initialX = shapeWidth + padding;
      computedPosition.initialY = shapeHeight + padding;
      computedPosition.vAlign = TEXT_POSITION.TOP.VALUE;
      computedPosition.align = TEXT_ALIGN.LEFT.VALUE;
      break;
    default:
      break;
  }

  return computedPosition;
}

export function computeTitleBounds(
  ctx: CanvasRenderingContext2D,
  state: BoundsState,
  shapeBounds: ShapeBounds,
): TitleBounds {
  const title = state.title === null ? '' : state.title;
  const stateWithTitle = { ...state, title };
  const { initialX: tX, initialY: tY, width: tW, height: tH, align: tAlign, isTextOverflow } =
    computeTitleData(ctx, stateWithTitle, shapeBounds);
  const titleBounds: TitleBounds = { x: tX, y: tY, width: tW, height: tH, isTextOverflow, align: tAlign };

  if (!title || state.fontSize === 0) {
    titleBounds.x = 0;
    titleBounds.y = 0;
    titleBounds.width = 0;
    titleBounds.height = 0;
  }

  return titleBounds;
}

interface AllBoundsResult {
  shapeBounds: ShapeBounds;
  shadowBounds: ShadowBounds;
  imageBounds: ImageBounds;
  titleBounds: TitleBounds;
  canvasWidth: number;
  canvasHeight: number;
}

export function computeAllBounds(ctx: CanvasRenderingContext2D, state: BoundsState): AllBoundsResult {
  const shapeBounds = computeShapeBounds(state);
  const shadowBounds = computeShadowBounds(state, shapeBounds);
  const imageBounds = computeMediaBounds(state, shapeBounds);
  const titleBounds = computeTitleBounds(ctx, state, shapeBounds);

  const { width: shapeWidth, height: shapeHeight } = shapeBounds;
  const { x: shadowX, y: shadowY, blurWidth: shadowBlurWidth } = shadowBounds;
  const { offsetX: mediaOffsetX, offsetY: mediaOffsetY } = imageBounds;
  const { x: titleX, y: titleY, width: titleWidth, height: titleHeight, isTextOverflow } = titleBounds;

  const shadowWidthLeft = shadowX! < 0 ? shadowX! : 0;
  const shadowWidthRight = shadowX! < 0 ? shadowBlurWidth! * 2 - Math.abs(shadowX!) : shadowBlurWidth! * 2 + shadowX!;
  const shadowHeightTop = shadowY! < 0 ? shadowY! : 0;
  const shadowHeightBottom = shadowY! < 0 ? shadowBlurWidth! * 2 - Math.abs(shadowY!) : shadowBlurWidth! * 2 + shadowY!;

  const titleWidthLeft = titleX! < 0 ? titleX! : 0;
  const isTitlePositionBottom = state.titlePosition === TEXT_POSITION.BOTTOM.VALUE;
  const isTitlePositionTop = state.titlePosition === TEXT_POSITION.TOP.VALUE;

  let titleWidthRight = titleX! > shapeWidth ? titleX! - shapeWidth + titleWidth : 0;
  if (isTextOverflow || isTitlePositionBottom || isTitlePositionTop) titleWidthRight = (titleWidth - shapeWidth) / 2;

  const titleHeightTop = titleY! < 0 ? titleY! : 0;
  let titleHeightBottom = (titleY! > shapeHeight || titleHeight > shapeHeight) ? titleY! - shapeHeight + titleHeight : 0;
  if (isTextOverflow) titleHeightBottom = (titleHeight - shapeHeight) / 2;

  const mediaWidthLeft = mediaOffsetX;
  const mediaWidthRight = Math.abs(mediaOffsetX);
  const mediaHeightTop = mediaOffsetY;
  const mediaHeightBottom = Math.abs(mediaOffsetY);

  const SHAPE_ID_HEIGHT = 12;
  const STATUS_LAYER_HEIGHT = 29 + 2;
  const NODE_MIN_WIDTH = 60 + 2;
  const computedEditedLayerWidth = state.editingAppearance || state.editingContent ? NODE_MIN_WIDTH : 0;
  const computedEditedLayerHeight = state.editingAppearance || state.editingContent ? STATUS_LAYER_HEIGHT : 0;

  const leftPart = Math.abs(Math.min(0, titleWidthLeft, shadowWidthLeft, mediaWidthLeft));
  const rightPart = Math.abs(Math.max(shadowWidthRight, titleWidthRight, mediaWidthRight, computedEditedLayerWidth));
  const topPart = Math.abs(Math.min(-SHAPE_ID_HEIGHT, shadowHeightTop, titleHeightTop, mediaHeightTop));
  const bottomPart = Math.abs(Math.max(shadowHeightBottom, titleHeightBottom, mediaHeightBottom, computedEditedLayerHeight));

  const canvasWidth = shapeWidth + leftPart + rightPart;
  const canvasHeight = shapeHeight + topPart + bottomPart;

  return { shapeBounds, shadowBounds, imageBounds, titleBounds, canvasWidth, canvasHeight };
}

export function computeShapeCoordinates(
  shapeBounds: ShapeBounds,
  shadowBounds: ShadowBounds,
  imageBounds: ImageBounds,
  titleBounds: TitleBounds,
): { x: number; y: number; shiftX: number; shiftY: number } {
  const SHAPE_ID_HEIGHT = 12;

  const { shadowShiftX, shadowShiftY, blurWidth: shadowBlurWidth } = shadowBounds;
  const { offsetX: mediaOffsetX, offsetY: mediaOffsetY } = imageBounds;
  const { x: titleX, y: titleY } = titleBounds;

  const shadowShiftXComputed = shadowShiftX! - shadowBlurWidth!;
  const shadowShiftYComputed = shadowShiftY! - shadowBlurWidth!;

  const shiftX = Math.abs(Math.min(0, shadowShiftXComputed, titleX!, mediaOffsetX));
  const shiftY = Math.abs(Math.min(-SHAPE_ID_HEIGHT, shadowShiftYComputed, titleY!, mediaOffsetY));

  return {
    x: shapeBounds.x + shiftX,
    y: shapeBounds.y + shiftY,
    shiftX,
    shiftY,
  };
}

export type { BoundsState };
